<template>
  <div class="pcp-page">
    <div class="pcp-topbar">
      <div class="pcp-container d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center gap-2">
          <img src="/assets/images/flockr_logo.png" alt="Flockr" style="width:32px;height:32px;" />
          <div>
            <div style="font-size:11px;color:#F5F0E8;opacity:.8;line-height:1;">Powered by</div>
            <div style="font-size:15px;font-weight:800;color:#F5F0E8;">Flockr Africa</div>
          </div>
        </div>
        <a href="/" class="btn btn-sm btn-outline-light"><i class="mdi mdi-open-in-new me-1"></i>flockr.africa</a>
      </div>
    </div>

    <div class="pcp-container" style="padding:24px 12px 80px;">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-warning"></div>
        <div class="mt-2" style="color:#6B6356;">Loading Passport…</div>
      </div>

      <div v-else-if="error" class="pcp-error-card">
        <i class="mdi mdi-shield-lock" style="font-size:44px;color:#dc2626;"></i>
        <h5 class="mt-3 fw-bold">Passport Unavailable</h5>
        <p style="color:#6B6356;">{{ error }}</p>
      </div>

      <template v-else-if="passport">
        <div class="pcp-hero">
          <div style="text-align:center;">
            <div class="pcp-verified">
              <i class="mdi mdi-shield-check"></i>Verified by Flockr
            </div>
            <div class="pcp-holder">{{ passport.holder.display_name || 'Passport Holder' }}</div>
            <div style="font-size:13px;color:#6B6356;">
              {{ passport.holder.phone_masked }} <span v-if="passport.holder.county"> · {{ passport.holder.county }}</span>
            </div>
            <div v-if="passport.label" style="font-size:12px;color:#8A827A;margin-top:2px;">
              Shared for: <strong>{{ passport.label }}</strong>
            </div>

            <svg viewBox="0 0 200 200" class="pcp-dial">
              <circle cx="100" cy="100" r="80" stroke="#E8E2DA" stroke-width="14" fill="none" />
              <circle cx="100" cy="100" r="80"
                      :stroke="passport.tier_colour" stroke-width="14" fill="none"
                      stroke-linecap="round" transform="rotate(-90 100 100)"
                      :stroke-dasharray="dialArc" />
              <text x="100" y="98" text-anchor="middle" font-size="42" font-weight="800" fill="#1A1A1A">{{ passport.score }}</text>
              <text x="100" y="122" text-anchor="middle" font-size="12" fill="#6B6356">out of 1000</text>
            </svg>
            <div class="pcp-tier-chip" :style="{background:passport.tier_colour}">
              <i class="mdi mdi-medal me-1"></i>{{ passport.tier }} Tier
            </div>
          </div>
        </div>

        <div class="pcp-card mt-3">
          <h6 class="pcp-card-title"><i class="mdi mdi-chart-donut me-1"></i>Score Breakdown</h6>
          <div v-for="p in passport.pillars" :key="p.code" class="pcp-pillar">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div style="font-size:13px;font-weight:600;">{{ p.label }} <span style="color:#6B6356;font-weight:400;">({{ p.weight_pct }}%)</span></div>
              <div style="font-size:12px;color:#6B6356;font-family:monospace;">{{ p.score }} / {{ p.max }}</div>
            </div>
            <div class="pcp-bar"><div class="pcp-bar-fill" :style="{width: (p.score/p.max*100) + '%', background: passport.tier_colour}"></div></div>
          </div>
        </div>

        <div class="pcp-card mt-3">
          <h6 class="pcp-card-title"><i class="mdi mdi-star-outline me-1"></i>Key Highlights</h6>
          <div class="pcp-highlights">
            <div><div class="pcp-hl-v">KES {{ fmt(passport.stats.gmv_kes) }}</div><div class="pcp-hl-l">Lifetime GMV</div></div>
            <div><div class="pcp-hl-v">{{ passport.stats.orders_12mo }}</div><div class="pcp-hl-l">Orders (12 mo)</div></div>
            <div><div class="pcp-hl-v">{{ passport.stats.stk_success_pct !== null ? passport.stats.stk_success_pct + '%' : '—' }}</div><div class="pcp-hl-l">Payment Success</div></div>
            <div><div class="pcp-hl-v">{{ passport.stats.avg_rating || '—' }}</div><div class="pcp-hl-l">Avg Rating</div></div>
            <div><div class="pcp-hl-v">{{ passport.stats.dispute_losses }}</div><div class="pcp-hl-l">Disputes Lost</div></div>
            <div><div class="pcp-hl-v">{{ ageDisplay }}</div><div class="pcp-hl-l">Member For</div></div>
          </div>
        </div>

        <div v-if="passport.badges && passport.badges.length" class="pcp-card mt-3">
          <h6 class="pcp-card-title"><i class="mdi mdi-trophy me-1"></i>Badges Earned</h6>
          <div class="pcp-badges">
            <div v-for="b in passport.badges" :key="b.code" class="pcp-badge">
              <i :class="b.icon" :style="{color: passport.tier_colour}"></i>
              <span>{{ b.label }}</span>
            </div>
          </div>
        </div>

        <div class="pcp-attest">
          <div>
            <strong>Live Passport.</strong> This score reflects real-time on-platform activity. It updates automatically as {{ firstName }} transacts, verifies, and grows their farm records.
          </div>
          <div style="font-size:11px;color:#6B6356;margin-top:6px;">
            Flockr Africa is licensed by the Office of the Data Protection Commissioner (ODPC), Republic of Kenya. This share link expires {{ fDate(passport.expires_at) }}.
          </div>
        </div>

        <div class="pcp-cta">
          <div style="font-size:14px;font-weight:600;">Are you a lending institution?</div>
          <div style="font-size:12px;color:#6B6356;margin-bottom:8px;">Integrate the Flockr Credit API to fetch verified passports for any Flockr farmer.</div>
          <a href="/developers" class="btn btn-sm btn-warning"><i class="mdi mdi-code-braces me-1"></i>Flockr Developer Portal</a>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, flocker } from '@/api';

export default {
  name: 'PublicCreditPassport',
  data() {
    return {
      loading: true,
      error: '',
      passport: null
    };
  },
  computed: {
    dialArc() {
      if (!this.passport) return '0 502';
      var c = 2 * Math.PI * 80;
      var pct = Math.max(0, Math.min(1, this.passport.score / 1000));
      return (pct * c) + ' ' + c;
    },
    firstName() {
      if (!this.passport || !this.passport.holder) return 'this farmer';
      return (this.passport.holder.display_name || '').split(' ')[0] || 'this farmer';
    },
    ageDisplay() {
      if (!this.passport) return '—';
      var d = this.passport.stats.account_age_days;
      if (d < 30) return d + 'd';
      if (d < 365) return Math.round(d/30) + ' mo';
      return (d/365).toFixed(1) + ' yr';
    }
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        var token = this.$route.params.token || '';
        var f = new FormData();
        f.append('function', 'getCreditPassportPublic');
        f.append('token', token);
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.passport = r.data.data;
          document.title = 'Flockr Credit Passport — ' + (this.passport.holder.display_name || '');
        } else {
          this.error = (r.data && r.data.message) || 'Unable to load Passport';
        }
      } catch (e) {
        this.error = 'Network error loading Passport';
      }
      this.loading = false;
    },
    fmt(n) { return new Intl.NumberFormat('en-KE').format(Math.round(Number(n) || 0)); },
    fDate(d) { if (!d) return ''; return new Date(d).toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' }); }
  }
};
</script>

<style scoped>
.pcp-page { min-height:100vh; background:#F7F5F2; }
.pcp-topbar { background:#1A1A1A; padding:12px 0; }
.pcp-container { max-width:800px; margin:0 auto; padding:0 12px; }

.pcp-hero { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:14px; padding:24px 16px; text-align:center; }
.pcp-verified { display:inline-flex; align-items:center; gap:4px; padding:3px 10px; background:rgba(22,163,74,.12); color:#16a34a; border-radius:20px; font-size:11px; font-weight:700; margin-bottom:8px; }
.pcp-holder { font-size:22px; font-weight:800; color:#1A1A1A; }
.pcp-dial { width:220px; height:220px; margin:12px auto 4px; display:block; }
.pcp-tier-chip { display:inline-flex; align-items:center; padding:5px 14px; border-radius:20px; color:#fff; font-weight:700; font-size:13px; }

.pcp-card { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:12px; padding:18px; }
.pcp-card-title { font-size:14px; font-weight:700; margin-bottom:12px; }

.pcp-pillar { margin-bottom:12px; }
.pcp-bar { height:8px; background:#EDE7DE; border-radius:6px; overflow:hidden; }
.pcp-bar-fill { height:100%; border-radius:6px; }

.pcp-highlights { display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; }
.pcp-hl-v { font-size:16px; font-weight:800; color:#BFA164; font-family:monospace; }
.pcp-hl-l { font-size:10px; color:#6B6356; text-transform:uppercase; letter-spacing:.5px; }

.pcp-badges { display:flex; flex-wrap:wrap; gap:8px; }
.pcp-badge { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; background:#F7F5F2; border-radius:20px; font-size:12px; font-weight:600; }

.pcp-attest { background:#FAF8F4; border-left:3px solid #BFA164; border-radius:8px; padding:12px 14px; margin-top:14px; font-size:12px; }

.pcp-cta { background:#1A1A1A; color:#F5F0E8; border-radius:12px; padding:18px; text-align:center; margin-top:16px; }
.pcp-cta div:first-child { color:#F5F0E8; }
.pcp-cta div:nth-child(2) { color:#A09888 !important; }

.pcp-error-card { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:14px; padding:40px 20px; text-align:center; max-width:400px; margin:40px auto; }
</style>
